import { useState, useEffect, useRef } from "react";
import { BASE_URL } from "../apiConfig";

export function useAutocomplete(query: string, minChars = 3, debounceMs = 250) {
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [activeSuggestion, setActiveSuggestion] = useState(-1);
  const controllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    const q = query.trim();
    if (q.length < minChars) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    const timer = setTimeout(async () => {
      controllerRef.current?.abort();
      controllerRef.current = new AbortController();
      try {
        const res = await fetch(
          `${BASE_URL}/api/autocomplete?q=${encodeURIComponent(q)}`,
          { signal: controllerRef.current.signal }
        );
        const data = await res.json();
        const list: string[] = Array.isArray(data?.suggestions)
          ? data.suggestions.slice(0, 8)
          : [];
        setSuggestions(list);
        setShowSuggestions(list.length > 0);
        setActiveSuggestion(-1);
      } catch (e: any) {
        if (e?.name !== "AbortError") setSuggestions([]);
      }
    }, debounceMs);

    return () => {
      clearTimeout(timer);
      controllerRef.current?.abort();
    };
  }, [query, minChars, debounceMs]);

  const clear = () => {
    setSuggestions([]);
    setShowSuggestions(false);
    setActiveSuggestion(-1);
  };

  const handleKeyDown = (
    e: React.KeyboardEvent,
    onSelect: (s: string) => void,
    onEnter: () => void
  ) => {
    if (!showSuggestions || suggestions.length === 0) {
      if (e.key === "Enter") onEnter();
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveSuggestion(i => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveSuggestion(i => Math.max(i - 1, -1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (activeSuggestion >= 0 && suggestions[activeSuggestion]) {
        onSelect(suggestions[activeSuggestion]);
      } else {
        onEnter();
      }
    } else if (e.key === "Escape") {
      clear();
    }
  };

  return { suggestions, showSuggestions, activeSuggestion, setShowSuggestions, clear, handleKeyDown };
}
